import { FlowVersionStatus } from "src/api/flowTypes";
import { useFlows } from "src/api/queries";
import { ConfirmationModal } from "src/base-components/ConfirmationModal";
import { ExternalLink } from "src/base-components/ExternalLink";
import { Pill } from "src/base-components/Pill";
import {
  FeatureMappingOutput,
  PreviewQueryRequestEnvironmentEnum,
} from "src/clients/features-control";
import { Accordion, useAccordionSingleMode } from "src/design-system/Accordion";
import { Query } from "src/featureCatalogue/utils";
import { useAllJobs } from "src/jobs/api/queries";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { LeftPaneOptions } from "src/router/SearchParams";
import {
  getBaseUrl,
  getUrlToAuthoringPage,
  getUrlToJobPage,
} from "src/router/urls";
import {
  entityNameToJsonRefPath,
  eventNameToJsonRefPath,
} from "src/schema/jsonRefUtils";

type ActiveQueryConfirmationModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  confirmationButtonText: string;
  description: string;
  variant?: "default" | "danger";
  query: Pick<Query, "event" | "columns">;
  environment: PreviewQueryRequestEnvironmentEnum;
};

export const ActiveQueryConfirmationModal: React.FC<
  ActiveQueryConfirmationModalProps
> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  confirmationButtonText,
  description,
  variant = "default",
  query,
  environment,
}) => {
  const { workspace, orgId } = useWorkspaceContext();

  const featureLifecycleEnabled = isFeatureFlagEnabled(
    FEATURE_FLAGS.featureLifecycle,
  );

  const entities = query.columns?.entities?.map((e) => e.entity_name) ?? [];
  const features = query.columns?.features ?? [];

  const { data: flows, isLoading: isFlowsLoading } = useFlows({
    workspaceId: workspace.id,
    includeSchemas: true,
  });

  const { data: jobs, isLoading: isJobsLoading } = useAllJobs(
    workspace.base_url!,
    {
      statuses: ["active"],
    },
  );

  const refsToCheck = new Set([
    ...entities.map(entityNameToJsonRefPath),
    eventNameToJsonRefPath(query.event),
  ]);

  const usedVersions = flows.flatMap((flow) =>
    flow.versions
      .filter(
        (version) =>
          version.status === FlowVersionStatus.PUBLISHED &&
          version?.input_schema?.properties &&
          hasMatchingRef(version.input_schema.properties, refsToCheck),
      )
      .map((version) => ({ flow, version })),
  );

  const usedJobs =
    jobs?.filter(
      (job) =>
        job.active_source?.configuration?.type === "entity" &&
        entities.includes(job.active_source.configuration.entity_schema),
    ) || [];

  const isLive = environment === PreviewQueryRequestEnvironmentEnum.LIVE;
  const baseUrl = getBaseUrl();

  const disableConfirmationButton =
    featureLifecycleEnabled && (isJobsLoading || isFlowsLoading);

  if (!isOpen) {
    return null;
  }

  return (
    <ConfirmationModal
      confirmationButtonText={confirmationButtonText}
      description={
        <div className="flex flex-col gap-y-4 font-inter-normal-13px">
          <span>{description}</span>
          {featureLifecycleEnabled && (
            <>
              <UsedFeaturesList features={features} />
              {isLive && (
                <>
                  <CollapsibleResourceList
                    isLoading={isFlowsLoading}
                    resources={usedVersions.map(({ flow, version }) => ({
                      id: version.id,
                      name: `${flow.name} ${version.name}`,
                      url: `${baseUrl}${getUrlToAuthoringPage(orgId, workspace.id, flow.id, version.id, { leftPane: LeftPaneOptions.Schema })}`,
                    }))}
                    title="Published Decision Flow(s)"
                    value="flows"
                  />
                  <CollapsibleResourceList
                    isLoading={isJobsLoading}
                    resources={usedJobs.map((job) => ({
                      id: job.id,
                      name: job.name,
                      url: `${baseUrl}${getUrlToJobPage(orgId, workspace.id, job.flow_id, job.id)}`,
                    }))}
                    title="Active Job(s)"
                    value="jobs"
                  />
                </>
              )}
            </>
          )}
        </div>
      }
      disableConfirmationButton={disableConfirmationButton}
      disableConfirmationButtonTooltip="Loading affected resources..."
      open={isOpen}
      title={title}
      variant={variant}
      onClose={onClose}
      onConfirm={onConfirm}
    />
  );
};

const hasMatchingRef = (
  properties: Record<
    string,
    { $ref?: string; anyOf?: { $ref?: string }[]; oneOf?: { $ref?: string }[] }
  >,
  refsToCheck: Set<string>,
) => {
  return Object.values(properties).some(
    (property) =>
      (property.$ref != null && refsToCheck.has(property.$ref)) ||
      (property.anyOf?.some((v) => v.$ref != null && refsToCheck.has(v.$ref)) ??
        false) ||
      (property.oneOf?.some((v) => v.$ref != null && refsToCheck.has(v.$ref)) ??
        false),
  );
};

const CollapsibleResourceList: React.FC<{
  title: string;
  resources: { id: string; name: string; url?: string }[];
  value: string;
  isLoading?: boolean;
}> = ({ title, resources, value, isLoading }) => {
  const { value: accordionValue, onChange } = useAccordionSingleMode();

  return (
    <Accordion value={accordionValue} onChange={onChange}>
      <Accordion.Item
        title={
          isLoading ? `Loading ${title}...` : `${resources.length} ${title}`
        }
        value={value}
      >
        <ul className="decideScrollbar max-h-40 list-inside list-disc overflow-y-auto">
          {resources.map((resource) => (
            <li key={resource.id}>
              {resource.url ? (
                <ExternalLink href={resource.url}>{resource.name}</ExternalLink>
              ) : (
                <span>{resource.name}</span>
              )}
            </li>
          ))}
        </ul>
      </Accordion.Item>
    </Accordion>
  );
};

export const UsedFeaturesList: React.FC<{
  features: FeatureMappingOutput[];
}> = ({ features }) => (
  <div className="flex flex-wrap gap-2">
    {features.map((feature) => (
      <Pill key={feature.feature_key} size="sm" variant="gray">
        <Pill.Text>{feature.feature_key}</Pill.Text>
      </Pill>
    ))}
  </div>
);
